import React, { useEffect, useState } from 'react';
import axios from 'axios';

import './AdminOrders.css';

const API_BASE = 'http://localhost:5001';

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  const [stats, setStats] = useState({
    totalSales: 0,
    monthlySales: 0,
    cancelledOrders: 0
  });

  const formatDate = (value) => {
    if (!value) return 'Date inconnue';
    try {
      return new Date(value).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (err) {
      return value;
    }
  };

  const formatCurrency = (amount) => `${(Number(amount) || 0).toFixed(2)} €`;

  const computeItemsCount = (products = []) => products.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const recomputeStats = (list) => {
    const totalSales = list
      .filter((order) => order.status === 'validated')
      .reduce((sum, order) => sum + (Number(order.total) || 0), 0);

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlySales = list
      .filter((order) => {
        const date = new Date(order.createdAt || order.updatedAt || 0);
        return order.status === 'validated' && date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      })
      .reduce((sum, order) => sum + (Number(order.total) || 0), 0);

    const cancelledOrders = list.filter((order) => order.status === 'cancelled').length;

    setStats({
      totalSales,
      monthlySales,
      cancelledOrders
    });
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/api/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? [...res.data] : [];
        data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setOrders(data);

        recomputeStats(data);
      } catch (err) {
        setError(err.response?.data?.error || 'Erreur lors du chargement des commandes.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleCancelOrder = async (orderId) => {
    const confirmation = window.confirm('Voulez-vous vraiment annuler cette commande ?');
    if (!confirmation) {
      return;
    }

    setActionMessage('');
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_BASE}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedOrder = res.data?.order || null;
      setOrders((prev) => {
        const updated = prev.map((order) => (order._id === orderId ? (updatedOrder || { ...order, status: 'cancelled' }) : order));
        recomputeStats(updated);
        return updated;
      });
      setActionMessage('Commande annulée avec succès.');
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible d’annuler la commande.');
    }
  };

  const validatedOrders = orders.filter((order) => order.status === 'validated');

  return (
    <div className="admin-orders">
      <header className="admin-orders__header">
        <div>
          <h3>Gestion des commandes</h3>
          <p>Consultez les commandes validées par les clients et annulez-les si nécessaire.</p>
        </div>
        <div className="admin-orders__metrics">
          <span className="admin-orders__metric">
            <strong>{formatCurrency(stats.totalSales)}</strong>
            <span>Total des ventes</span>
          </span>
          <span className="admin-orders__metric">
            <strong>{formatCurrency(stats.monthlySales)}</strong>
            <span>Ventes du mois</span>
          </span>
          <span className="admin-orders__metric">
            <strong>{stats.cancelledOrders}</strong>
            <span>Commandes annulées</span>
          </span>
        </div>
        {actionMessage && <span className="admin-orders__message admin-orders__message--success">{actionMessage}</span>}
        {error && <span className="admin-orders__message admin-orders__message--error">{error}</span>}
      </header>

      {loading ? (
        <p>Chargement des commandes…</p>
      ) : validatedOrders.length === 0 ? (
        <p>Aucune commande validée pour le moment.</p>
      ) : (
        <div className="admin-orders__table-wrapper">
          <table className="admin-orders__table">
            <thead>
              <tr>
                <th>Commande</th>
                <th>Client</th>
                <th>Date</th>
                <th>Articles</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {validatedOrders.map((order) => (
                <tr key={order._id}>
                  <td>
                    <span className="admin-orders__order-id">#{order._id?.slice(-8)}</span>
                  </td>
                  <td>
                    <div className="admin-orders__customer">
                      <strong>{order.user?.companyName || order.user?.email || 'Client'}</strong>
                      <small>{order.user?.email}</small>
                    </div>
                  </td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{computeItemsCount(order.products)} article(s)</td>
                  <td>{formatCurrency(order.total)}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-orders__cancel-btn"
                      onClick={() => handleCancelOrder(order._id)}
                    >
                      Annuler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
